import React from "react";
import { type RouteComponentProps, withRouter } from "react-router-dom";

import { faChevronLeft, faExternalLink } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { faArrowAltCircleUp } from "@awesome.me/kit-4c83d50d0d/icons/classic/solid";
import type { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";
import { memoize } from "lodash";
import uuidv4 from "uuid/v4";

import SettingsPageHeader from "~/account/SettingsPageHeader";
import { bookSalesCall, type FeatureValues, INTEGRATIONS_HELP_ARTICLE_ID, Products } from "~/billing/utils";
import { HeaderAction } from "~/common/PageHeader";
import { ScrollContext } from "~/layout/ScrollContext";
import ScrollToTop from "~/lib/scrollToTop";
import { dispatchAnalyticsTrack } from "~/lib/segment";
import DocumentTitle from "~/lib/title";
import { type ArticleID, showHelpCenter } from "~/lib/utils";
import { DropdownItem } from "~/ui/ArrowDropdown";
import { Button } from "~/ui/button";
import { HelpCenterLink } from "~/ui/legacyFellowIndex";
import Tooltip from "~/ui/Tooltip";

import { ConnectionLabel } from "../Card";

import stylesTemplateGrid from "~/feedback/css/TemplateGrid.module.css";

import styles from "./Settings.module.css";

export const SettingsPageContent: React.FunctionComponent<{
	children: React.ReactNode;
	className?: string;
}> = props => {
	return <div className={cx(styles.whiteContainer, props.className)}>{props.children}</div>;
};

type Props = RouteComponentProps & {
	name: string;
	connected: boolean;
	connectedURL?: string;
	logo?: React.ReactNode;
	installText?: string;
	tabs: Array<{ label: string; key: string }> | null;
	activeTab: string | null;
	paywalledButton?: "fellow_pro_ai" | "fellow_business_ai" | "fellow_enterprise_ai";
	paywalledBanner?: "fellow_pro_ai" | "fellow_business_ai" | "fellow_enterprise_ai";
	paywallFeature: FeatureValues | null;
	isCustomConnect?: boolean;
	disabledMessage?: string;
	onTabClick: (tab: any) => void; // eslint-disable-line @typescript-eslint/no-explicit-any
	onInstall?: () => void;
	onDisconnect: () => void;
	onUpgradeClick?: () => void;
	children?: React.ReactNode;
	backPath: string;
	onBackClick?: () => void;
	installIcon?: IconDefinition;
	articleId?: ArticleID;
	articleIdOnPaywall?: ArticleID;
	showMoreActions?: boolean;
	hideConnectionLabel?: boolean;
	upgradeText?: React.ReactNode;
};

export default withRouter(function Settings({
	logo,
	name,
	children,
	installText,
	history,
	backPath,
	onBackClick,
	tabs,
	activeTab,
	onInstall,
	onUpgradeClick,
	onDisconnect,
	onTabClick,
	connected,
	connectedURL,
	paywalledButton,
	paywalledBanner,
	disabledMessage,
	installIcon,
	isCustomConnect,
	paywallFeature,
	articleId,
	showMoreActions = true,
	articleIdOnPaywall = INTEGRATIONS_HELP_ARTICLE_ID,
	hideConnectionLabel = false,
	upgradeText,
}: Props) {
	const analyticsProperties = React.useMemo(
		() => ({
			fingerprint: uuidv4(),
			ux: "banner",
			feature: paywallFeature,
		}),
		[paywallFeature],
	);

	React.useEffect(() => {
		if (!!paywalledBanner) {
			dispatchAnalyticsTrack("Paywall Encountered", analyticsProperties);
		}
	}, [analyticsProperties, paywalledBanner]);

	const onClickTab = memoize(tab => (e: React.MouseEvent) => {
		e.preventDefault();
		onTabClick(tab);
	});

	const onLearnMoreOnPaywallClicked = React.useCallback(() => {
		showHelpCenter(articleIdOnPaywall);
		dispatchAnalyticsTrack("Paywall Action Taken", { ...analyticsProperties, action: "redirect_to_marketing" });
	}, [analyticsProperties, articleIdOnPaywall]);

	const onUpgradeButtonClicked = React.useCallback(() => {
		if (onUpgradeClick) return onUpgradeClick();
		if (paywalledBanner === "fellow_pro_ai") {
			history.push("/account/billing/plan/?from=upgrade");
			dispatchAnalyticsTrack("Paywall Action Taken", { ...analyticsProperties, action: "redirect_to_billing" });
		} else {
			// eslint-disable-next-line camelcase
			bookSalesCall(paywallFeature ? { utm_campaign: paywallFeature } : {});
			dispatchAnalyticsTrack("Paywall Action Taken", { ...analyticsProperties, action: "book_sales_call" });
		}
	}, [analyticsProperties, history, onUpgradeClick, paywallFeature, paywalledBanner]);

	const buttonProduct = paywalledButton ? Products[paywalledButton] : undefined;
	const bannerProduct = paywalledBanner ? Products[paywalledBanner] : undefined;

	const pageSubject = (
		<div className={styles.pageHeaderSubject}>
			{logo && <div className={styles.logo}>{logo}</div>}
			<div className={styles.name}>{name}</div>
			{connected && !paywalledBanner && !hideConnectionLabel && (
				<ConnectionLabel connected className={styles.connectedLabel} />
			)}
			{connected && connectedURL && (
				<div className={styles.connectedURL}>
					<a href={connectedURL} target="_blank" rel="noopener noreferrer">
						{connectedURL}
						<FontAwesomeIcon icon={faExternalLink} />
					</a>
				</div>
			)}
		</div>
	);

	const moreActions =
		connected && showMoreActions
			? [
					<DropdownItem key="disconnect" onClick={onDisconnect} className={styles.dropdownItem}>
						Disconnect…
					</DropdownItem>,
				]
			: [];

	const headerActionContent = (
		<>
			{paywalledButton && articleId && <HelpCenterLink articleId={articleId}>Learn more</HelpCenterLink>}
			<HeaderAction
				variant="primary"
				onClick={onInstall}
				className={styles.headerAction}
				disabled={Boolean(disabledMessage)}
			>
				{!buttonProduct && installIcon && (
					<FontAwesomeIcon
						className={cx(styles.installIcon, { [styles.installIconDisabled]: Boolean(disabledMessage) })}
						icon={installIcon}
					/>
				)}
				{buttonProduct && <FontAwesomeIcon className={styles.crownIcon} icon={buttonProduct.icon} />}
				{installText}
			</HeaderAction>
		</>
	);

	let headerAction: React.ReactNode[] = [];
	if (!connected && !paywalledBanner) {
		headerAction = Boolean(disabledMessage)
			? [
					<Tooltip key="header-action-content" placement="bottom" content={disabledMessage}>
						<span>{headerActionContent}</span>
					</Tooltip>,
				]
			: [headerActionContent];
	}

	return (
		<>
			<ScrollContext.Consumer>{context => <ScrollToTop context={context} />}</ScrollContext.Consumer>

			<DocumentTitle title={`${name} Integration Settings`} />
			<SettingsPageHeader
				subject={pageSubject}
				actions={isCustomConnect ? [] : headerAction}
				moreActions={moreActions}
				text="Actions"
				backButton={
					<div className={styles.backButton} onClick={onBackClick ?? (() => history.push(backPath))}>
						<FontAwesomeIcon icon={faChevronLeft} />
					</div>
				}
			/>
			{tabs && tabs.length > 0 && !paywalledBanner && (
				<nav className={styles.tabBar}>
					{tabs.map(tab => (
						<a
							key={tab.key}
							href="#"
							className={cx(stylesTemplateGrid.tab, styles.tab, {
								[stylesTemplateGrid.tabActive]: activeTab == tab.key,
							})}
							onClick={onClickTab(tab.key)}
						>
							{tab.label}
						</a>
					))}
				</nav>
			)}
			{paywalledBanner && bannerProduct && (
				<div className={styles.paywallBanner}>
					<span className={styles.paywallText}>
						{upgradeText || (
							<>
								Upgrade your plan to unlock the <b>{name} integration</b>.
							</>
						)}
					</span>
					<div className={styles.paywallButtons}>
						<Button
							size="wide"
							variant="secondary"
							className={styles.learnMoreButton}
							onClick={onLearnMoreOnPaywallClicked}
						>
							Learn more
						</Button>
						<Button size="wide" className={styles.upgradeButton} variant="billing" onClick={onUpgradeButtonClicked}>
							<FontAwesomeIcon icon={faArrowAltCircleUp} />
							&nbsp; Upgrade
						</Button>
					</div>
				</div>
			)}
			{children}
		</>
	);
});
